import { resolveProfile, isLegacyIdentity, type ProfileId } from '../../lib/profileIdentity';
import { normalizeRoleName } from '../../lib/roles';

/**
 * Role-based dashboard layouts — single source of truth.
 *
 * Consumed by:
 *   - DashboardPage.tsx (Squad Charlie) → routes user to correct layout
 *   - widgetRegistry.ts (Squad Bravo) → registers all widgetKeys referenced here
 *
 * Authoring rules (see docs/ROLE-DASHBOARDS-SPEC.md):
 *   - Top row (h=1) = "Am I on track today?" urgency metrics
 *   - Row 2 = rolling week/month KPIs
 *   - Row 3+ = trends / deep-dive charts
 *   - `scope` controls whose numbers a widget shows — enforced on backend via JWT
 *   - `missing: true` means Squad Bravo needs to build widget + backend endpoint
 *   - `requiresBackend` names the endpoint path Bravo must implement
 *
 * Grid is 4-column. Cards are w=1 h=1 by default; charts are w=2..4 h=2.
 */

/**
 * Apr 24, 2026 — `SALES_EXEC_FIELD` and `SALES_EXEC_OFFICE` were collapsed
 * into a single `SALES_EXECUTIVE` role. The differentiation was creating
 * unnecessary nesting without meaningful UI divergence. The unified layout
 * inherits from the richer FIELD layout and folds in OFFICE's desk widgets.
 * TELECALLER stays separate (distinct workflow).
 *
 * Any legacy caller that still passes 'SALES_EXEC_FIELD' / 'SALES_EXEC_OFFICE'
 * will hit a TS error — resolve by updating to 'SALES_EXECUTIVE'.
 */
export type DashboardRoleKey =
  | 'CRM_OPERATIONS'
  | 'PUF_OPERATOR'
  | 'PACKING_OPERATOR'
  | 'QA_MANAGER'
  | 'QA_INSPECTOR'
  | 'DISPATCH_MANAGER'
  | 'LOADER'
  | 'SECURITY'
  | 'HR'
  | 'UNKNOWN'
  | 'OWNER'
  | 'SALES_HEAD'
  | 'SALES_MANAGER'
  | 'SALES_EXECUTIVE'
  | 'LEAD_MANAGER' // Apr 28, 2026 — verifies/edits/reassigns leads, sees ALL.
  | 'LEAD_TRIAGE_COORDINATOR' // May 26, 2026 — Deepika role: ONLY triage dashboard + profile.
  | 'BACKEND_STAFF' // Apr 28, 2026 — alias for TELECALLER UI; same dashboard for now.
  | 'PRODUCTION_HEAD'
  | 'PRODUCTION_SUPERVISOR'
  | 'INVENTORY_HEAD'
  | 'PURCHASE_HEAD'
  | 'SERVICE_MANAGER'
  | 'SERVICE_TECHNICIAN'
  | 'FINANCE_MANAGER'
  | 'TELECALLER'
  | 'WAREHOUSE_EXECUTIVE'
  | 'ACCOUNTS_EXECUTIVE'
  | 'FIELD_PARTNER';

export type WidgetScope = 'MY' | 'TEAM' | 'DEPT' | 'ORG';

export interface RoleWidget {
  /** Matches `WIDGET_REGISTRY[n].id` in widgetRegistry.ts */
  widgetKey: string;
  /** Columns on the 4-col grid */
  w: 1 | 2 | 3 | 4;
  /** Rows (1 = stat card, 2 = chart/table) */
  h: 1 | 2;
  /** Whose numbers this widget shows — backend reads this via ?scope= */
  scope: WidgetScope;
  /** If widget needs a new backend endpoint, name it here (Bravo implements) */
  requiresBackend?: string;
  /** True iff widgetKey doesn't exist in widgetRegistry.ts today (Bravo builds) */
  missing?: true;
}

export interface RoleLayout {
  role: DashboardRoleKey;
  title: string;
  subtitle: string;
  /** Ordered widgets — rendered left→right, top→bottom on a 4-col grid */
  widgets: RoleWidget[];
}

/** Minimal user shape needed by resolveRoleKey */
export interface UserForRoleResolution {
  role?: { name?: string | null } | null;
  department?: { name?: string | null; code?: string | null } | null;
  designation?: string | null;
  tenantId?: string | null;
}

/* ---------------------------------------------------------------------- */
/*  Layouts                                                                */
/* ---------------------------------------------------------------------- */

export const ROLE_LAYOUTS: Record<DashboardRoleKey, RoleLayout> = {
  CRM_OPERATIONS: { role: 'CRM_OPERATIONS', title: 'CRM Operations', subtitle: '', widgets: [] },
  PUF_OPERATOR: { role: 'PUF_OPERATOR', title: 'My Shift · PUF', subtitle: '', widgets: [] },
  PACKING_OPERATOR: { role: 'PACKING_OPERATOR', title: 'My Shift · Packing', subtitle: '', widgets: [] },
  QA_MANAGER: { role: 'QA_MANAGER', title: 'Quality Command', subtitle: '', widgets: [] },
  QA_INSPECTOR: { role: 'QA_INSPECTOR', title: 'My Inspections', subtitle: '', widgets: [] },
  DISPATCH_MANAGER: { role: 'DISPATCH_MANAGER', title: 'Dispatch Shift', subtitle: '', widgets: [] },
  LOADER: { role: 'LOADER', title: 'My Load', subtitle: '', widgets: [] },
  SECURITY: { role: 'SECURITY', title: 'My Gate', subtitle: '', widgets: [] },
  HR: { role: 'HR', title: 'People Desk', subtitle: '', widgets: [] },
  UNKNOWN: { role: 'UNKNOWN', title: 'No access', subtitle: '', widgets: [] },

  /* ── OWNER ──────────────────────────────────────────────────────────── */
  OWNER: {
    role: 'OWNER',
    title: 'Owner Command Centre',
    subtitle: 'The whole business on one page.',
    widgets: [
      // Row 1 — today/urgency
      { widgetKey: 'sales.active_deals',           w: 1, h: 1, scope: 'ORG' },
      { widgetKey: 'finance.invoices_overdue',     w: 1, h: 1, scope: 'ORG' },
      { widgetKey: 'service.open_tickets',         w: 1, h: 1, scope: 'ORG' },
      { widgetKey: 'inventory.below_min_stock',    w: 1, h: 1, scope: 'ORG' },
      // Row 2 — rolling
      { widgetKey: 'sales.pipeline_value',         w: 1, h: 1, scope: 'ORG' },
      { widgetKey: 'sales.revenue_achieved',       w: 1, h: 1, scope: 'ORG' },
      { widgetKey: 'sales.outstanding',            w: 1, h: 1, scope: 'ORG' },
      { widgetKey: 'sales.win_rate',               w: 1, h: 1, scope: 'ORG' },
      { widgetKey: 'owner.dso',                    w: 1, h: 1, scope: 'ORG' },
      { widgetKey: 'owner.revenue_per_employee',   w: 1, h: 1, scope: 'ORG' },
      { widgetKey: 'owner.oee',                    w: 1, h: 1, scope: 'ORG' },
      { widgetKey: 'owner.warranty_claim_rate',    w: 1, h: 1, scope: 'ORG' },
      // Row 3 — operational
      { widgetKey: 'inventory.total_value',        w: 1, h: 1, scope: 'ORG' },
      { widgetKey: 'inventory.total_skus',         w: 1, h: 1, scope: 'ORG' },
      { widgetKey: 'finance.revenue_invoiced',     w: 1, h: 1, scope: 'ORG' },
      { widgetKey: 'owner.delivery_sla',           w: 1, h: 1, scope: 'ORG' },
      // Row 4 — chart + top deals
      { widgetKey: 'charts.monthly_revenue',       w: 2, h: 2, scope: 'ORG' },
      { widgetKey: 'sales.top_deals_closing',      w: 2, h: 2, scope: 'ORG', missing: true, requiresBackend: 'GET /api/v1/analytics/sales/top-deals-closing?limit=5&scope=ORG' },
      // Row 5 — cash + burn
      { widgetKey: 'finance.cash_position',        w: 1, h: 1, scope: 'ORG', missing: true, requiresBackend: 'GET /api/v1/analytics/finance/cash-position?scope=ORG' },
      { widgetKey: 'production.wip_value',         w: 1, h: 1, scope: 'ORG', missing: true, requiresBackend: 'GET /api/v1/analytics/production/wip?scope=ORG' },
      { widgetKey: 'service.sla_breach_rate',      w: 1, h: 1, scope: 'ORG', missing: true, requiresBackend: 'GET /api/v1/analytics/service/sla-breach?scope=ORG' },
      { widgetKey: 'inventory.turns',              w: 1, h: 1, scope: 'ORG', missing: true, requiresBackend: 'GET /api/v1/analytics/inventory/turns?scope=ORG' },
    ],
  },

  /* ── SALES_HEAD ─────────────────────────────────────────────────────── */
  SALES_HEAD: {
    role: 'SALES_HEAD',
    title: 'Sales Command Centre',
    subtitle: 'Your full sales org, by team and by rep.',
    widgets: [
      // Row 1 — urgency (team-wide)
      { widgetKey: 'sales.overdue_followups',      w: 1, h: 1, scope: 'DEPT', missing: true, requiresBackend: 'GET /api/v1/analytics/sales/overdue-followups?scope=DEPT' },
      { widgetKey: 'sales.deals_slipping',         w: 1, h: 1, scope: 'DEPT', missing: true, requiresBackend: 'GET /api/v1/analytics/sales/deals-slipping?scope=DEPT' },
      { widgetKey: 'sales.active_deals',           w: 1, h: 1, scope: 'DEPT' },
      { widgetKey: 'finance.invoices_overdue',     w: 1, h: 1, scope: 'DEPT' },
      // Row 2 — rolling
      { widgetKey: 'sales.pipeline_value',         w: 1, h: 1, scope: 'DEPT' },
      { widgetKey: 'sales.win_rate',               w: 1, h: 1, scope: 'DEPT' },
      { widgetKey: 'sales.revenue_achieved',       w: 1, h: 1, scope: 'DEPT' },
      { widgetKey: 'sales.outstanding',            w: 1, h: 1, scope: 'DEPT' },
      // Row 3 — leaderboards + mix
      { widgetKey: 'sales.quota_attainment_by_manager', w: 2, h: 2, scope: 'DEPT', missing: true, requiresBackend: 'GET /api/v1/analytics/sales/quota-attainment?groupBy=manager&scope=DEPT' },
      { widgetKey: 'sales.lead_source_mix',        w: 2, h: 2, scope: 'DEPT', missing: true, requiresBackend: 'GET /api/v1/analytics/sales/lead-source-mix?scope=DEPT' },
      // Row 5 — forecast + losses
      { widgetKey: 'sales.revenue_forecast',       w: 2, h: 2, scope: 'DEPT', missing: true, requiresBackend: 'GET /api/v1/analytics/sales/revenue-forecast?scope=DEPT' },
      { widgetKey: 'sales.loss_reasons',           w: 2, h: 2, scope: 'DEPT', missing: true, requiresBackend: 'GET /api/v1/analytics/sales/loss-reasons?scope=DEPT' },
    ],
  },

  /* ── SALES_MANAGER ──────────────────────────────────────────────────── */
  SALES_MANAGER: {
    role: 'SALES_MANAGER',
    title: 'Team Sales Dashboard',
    subtitle: 'Your team — pipeline, activity, conversion.',
    widgets: [
      // Row 1 — urgency (my team)
      { widgetKey: 'sales.overdue_followups',      w: 1, h: 1, scope: 'TEAM', missing: true, requiresBackend: 'GET /api/v1/analytics/sales/overdue-followups?scope=TEAM' },
      { widgetKey: 'sales.deals_slipping',         w: 1, h: 1, scope: 'TEAM', missing: true, requiresBackend: 'GET /api/v1/analytics/sales/deals-slipping?scope=TEAM' },
      { widgetKey: 'sales.team_activities_today',  w: 1, h: 1, scope: 'TEAM', missing: true, requiresBackend: 'GET /api/v1/analytics/sales/team-activities-today?scope=TEAM' },
      { widgetKey: 'sales.active_deals',           w: 1, h: 1, scope: 'TEAM' },
      // Row 2 — rolling (my team)
      { widgetKey: 'sales.pipeline_value',         w: 1, h: 1, scope: 'TEAM' },
      { widgetKey: 'sales.win_rate',               w: 1, h: 1, scope: 'TEAM' },
      { widgetKey: 'sales.revenue_achieved',       w: 1, h: 1, scope: 'TEAM' },
      { widgetKey: 'sales.outstanding',            w: 1, h: 1, scope: 'TEAM' },
      // Row 3 — leaderboard + stage conversion
      { widgetKey: 'sales.rep_leaderboard',        w: 2, h: 2, scope: 'TEAM', missing: true, requiresBackend: 'GET /api/v1/analytics/sales/rep-leaderboard?scope=TEAM' },
      { widgetKey: 'sales.stage_conversion',       w: 2, h: 2, scope: 'TEAM', missing: true, requiresBackend: 'GET /api/v1/analytics/sales/stage-conversion?scope=TEAM' },
    ],
  },

  /* ── SALES_EXECUTIVE (unified — was SALES_EXEC_FIELD + SALES_EXEC_OFFICE) */
  /* Apr 24, 2026 — merged. Inherits the richer FIELD layout + adds the
   * OFFICE-only "quotes_pending" and "outstanding_on_my_accounts" widgets
   * so desk-bound execs see the same surface as field execs. */
  SALES_EXECUTIVE: {
    role: 'SALES_EXECUTIVE',
    title: 'My Day',
    subtitle: 'Your leads, your calls, your accounts.',
    widgets: [
      // Row 1 — today
      { widgetKey: 'my.calls_today',               w: 1, h: 1, scope: 'MY', missing: true, requiresBackend: 'GET /api/v1/analytics/my-productivity/calls-today' },
      { widgetKey: 'my.next_followups',            w: 1, h: 1, scope: 'MY', missing: true, requiresBackend: 'GET /api/v1/analytics/my-productivity/next-followups?limit=5' },
      { widgetKey: 'my.proposals_pending',         w: 1, h: 1, scope: 'MY', missing: true, requiresBackend: 'GET /api/v1/analytics/my-productivity/proposals-pending' },
      { widgetKey: 'my.quotes_pending',            w: 1, h: 1, scope: 'MY', missing: true, requiresBackend: 'GET /api/v1/analytics/my-productivity/quotes-pending' },
      // Row 2 — rolling
      { widgetKey: 'my.pipeline_value',            w: 1, h: 1, scope: 'MY' },
      { widgetKey: 'my.win_rate',                  w: 1, h: 1, scope: 'MY' },
      { widgetKey: 'my.deals_open',                w: 1, h: 1, scope: 'MY' },
      { widgetKey: 'my.deals_won',                 w: 1, h: 1, scope: 'MY' },
      // Row 3 — map + activity
      { widgetKey: 'my.geo_checkins_map',          w: 2, h: 2, scope: 'MY', missing: true, requiresBackend: 'GET /api/v1/analytics/my-productivity/geo-checkins?days=7' },
      { widgetKey: 'my.activities_this_month',     w: 1, h: 1, scope: 'MY' },
      { widgetKey: 'my.outstanding_on_my_accounts', w: 1, h: 1, scope: 'MY', missing: true, requiresBackend: 'GET /api/v1/analytics/my-productivity/outstanding-my-accounts' },
    ],
  },

  /* ── PRODUCTION_HEAD ────────────────────────────────────────────────── */
  PRODUCTION_HEAD: {
    role: 'PRODUCTION_HEAD',
    title: 'Production Command Centre',
    subtitle: 'Shop-floor pulse — orders, OEE, QC, bottlenecks.',
    widgets: [
      // Row 1 — urgency (today)
      { widgetKey: 'production.orders_at_risk',    w: 1, h: 1, scope: 'DEPT', missing: true, requiresBackend: 'GET /api/v1/analytics/production/orders-at-risk?scope=DEPT' },
      { widgetKey: 'production.open_orders',       w: 1, h: 1, scope: 'DEPT', missing: true, requiresBackend: 'GET /api/v1/analytics/production/open-orders?scope=DEPT' },
      { widgetKey: 'production.rm_shortages',      w: 1, h: 1, scope: 'DEPT', missing: true, requiresBackend: 'GET /api/v1/analytics/production/rm-shortages?scope=DEPT' },
      { widgetKey: 'production.daily_output',      w: 1, h: 1, scope: 'DEPT' },
      // Row 2 — rolling
      { widgetKey: 'production.target_achievement', w: 1, h: 1, scope: 'DEPT' },
      { widgetKey: 'owner.oee',                    w: 1, h: 1, scope: 'DEPT' },
      { widgetKey: 'production.defect_rate',       w: 1, h: 1, scope: 'DEPT' },
      { widgetKey: 'production.machine_utilization', w: 1, h: 1, scope: 'DEPT' },
      // Row 3 — quality + rejections
      { widgetKey: 'production.qc_pass_rate',      w: 1, h: 1, scope: 'DEPT', missing: true, requiresBackend: 'GET /api/v1/analytics/production/qc-pass-rate?scope=DEPT' },
      { widgetKey: 'production.rejections_week',   w: 1, h: 1, scope: 'DEPT', missing: true, requiresBackend: 'GET /api/v1/analytics/production/rejections?period=week&scope=DEPT' },
      { widgetKey: 'production.shift_utilization', w: 1, h: 1, scope: 'DEPT', missing: true, requiresBackend: 'GET /api/v1/analytics/production/shift-utilization?scope=DEPT' },
      { widgetKey: 'production.wip_value',         w: 1, h: 1, scope: 'DEPT', missing: true, requiresBackend: 'GET /api/v1/analytics/production/wip?scope=DEPT' },
      // Row 4 — bottleneck chart
      { widgetKey: 'production.bottleneck_workcentres', w: 4, h: 2, scope: 'DEPT', missing: true, requiresBackend: 'GET /api/v1/analytics/production/bottleneck-workcentres?top=5&scope=DEPT' },
    ],
  },

  /* ── PRODUCTION_SUPERVISOR ──────────────────────────────────────────── */
  PRODUCTION_SUPERVISOR: {
    role: 'PRODUCTION_SUPERVISOR',
    title: 'My Shift',
    subtitle: 'Your line. Your output. Your queue.',
    widgets: [
      // Row 1 — today
      { widgetKey: 'my.shift_output_today',        w: 1, h: 1, scope: 'MY', missing: true, requiresBackend: 'GET /api/v1/analytics/my-productivity/shift-output-today' },
      { widgetKey: 'my.line_efficiency',           w: 1, h: 1, scope: 'MY', missing: true, requiresBackend: 'GET /api/v1/analytics/my-productivity/line-efficiency' },
      { widgetKey: 'my.rejections_today',          w: 1, h: 1, scope: 'MY', missing: true, requiresBackend: 'GET /api/v1/analytics/my-productivity/rejections-today' },
      { widgetKey: 'my.team_attendance',           w: 1, h: 1, scope: 'MY', missing: true, requiresBackend: 'GET /api/v1/analytics/my-productivity/team-attendance' },
      // Row 2 — queue
      { widgetKey: 'my.workorders_queued',         w: 2, h: 2, scope: 'MY', missing: true, requiresBackend: 'GET /api/v1/analytics/my-productivity/workorders-queued?limit=5' },
      { widgetKey: 'my.material_ready_check',      w: 2, h: 2, scope: 'MY', missing: true, requiresBackend: 'GET /api/v1/analytics/my-productivity/material-ready' },
    ],
  },

  /* ── INVENTORY_HEAD ─────────────────────────────────────────────────── */
  INVENTORY_HEAD: {
    role: 'INVENTORY_HEAD',
    title: 'Inventory Control Centre',
    subtitle: 'Stock value, stock-outs, dispatches, accuracy.',
    widgets: [
      // Row 1 — urgency
      { widgetKey: 'inventory.stockouts_count',    w: 1, h: 1, scope: 'DEPT', missing: true, requiresBackend: 'GET /api/v1/analytics/inventory/stockouts?scope=DEPT' },
      { widgetKey: 'inventory.below_min_stock',    w: 1, h: 1, scope: 'DEPT' },
      { widgetKey: 'inventory.pending_grns',       w: 1, h: 1, scope: 'DEPT', missing: true, requiresBackend: 'GET /api/v1/analytics/inventory/pending-grns?scope=DEPT' },
      { widgetKey: 'inventory.movements_today',    w: 1, h: 1, scope: 'DEPT', missing: true, requiresBackend: 'GET /api/v1/analytics/inventory/movements-today?scope=DEPT' },
      // Row 2 — rolling
      { widgetKey: 'inventory.total_value',        w: 1, h: 1, scope: 'DEPT' },
      { widgetKey: 'inventory.total_skus',         w: 1, h: 1, scope: 'DEPT' },
      { widgetKey: 'inventory.turns',              w: 1, h: 1, scope: 'DEPT', missing: true, requiresBackend: 'GET /api/v1/analytics/inventory/turns?scope=DEPT' },
      { widgetKey: 'inventory.accuracy_pct',       w: 1, h: 1, scope: 'DEPT', missing: true, requiresBackend: 'GET /api/v1/analytics/inventory/accuracy?scope=DEPT' },
      // Row 3 — deep-dive
      { widgetKey: 'inventory.days_of_inventory',  w: 2, h: 2, scope: 'DEPT', missing: true, requiresBackend: 'GET /api/v1/analytics/inventory/days-of-inventory?groupBy=category&scope=DEPT' },
      { widgetKey: 'inventory.slow_moving_value',  w: 1, h: 1, scope: 'DEPT', missing: true, requiresBackend: 'GET /api/v1/analytics/inventory/slow-moving?scope=DEPT' },
      { widgetKey: 'inventory.recent_dispatches',  w: 1, h: 1, scope: 'DEPT', missing: true, requiresBackend: 'GET /api/v1/analytics/inventory/recent-dispatches?limit=5&scope=DEPT' },
    ],
  },

  /* ── PURCHASE_HEAD ──────────────────────────────────────────────────── */
  PURCHASE_HEAD: {
    role: 'PURCHASE_HEAD',
    title: 'Procurement Control Centre',
    subtitle: 'Open POs, vendor performance, spend.',
    widgets: [
      // Row 1 — urgency
      { widgetKey: 'procurement.open_pos',         w: 1, h: 1, scope: 'DEPT', missing: true, requiresBackend: 'GET /api/v1/analytics/procurement/open-pos?scope=DEPT' },
      { widgetKey: 'procurement.pending_grns',     w: 1, h: 1, scope: 'DEPT', missing: true, requiresBackend: 'GET /api/v1/analytics/procurement/pending-grns?scope=DEPT' },
      { widgetKey: 'procurement.vendor_overdue',   w: 1, h: 1, scope: 'DEPT', missing: true, requiresBackend: 'GET /api/v1/analytics/procurement/vendor-overdue?scope=DEPT' },
      { widgetKey: 'procurement.rfqs_awaiting_award', w: 1, h: 1, scope: 'DEPT', missing: true, requiresBackend: 'GET /api/v1/analytics/procurement/rfqs-awaiting-award?scope=DEPT' },
      // Row 2 — rolling
      { widgetKey: 'procurement.avg_cycle_days',   w: 1, h: 1, scope: 'DEPT', missing: true, requiresBackend: 'GET /api/v1/analytics/procurement/avg-cycle-days?scope=DEPT' },
      { widgetKey: 'procurement.spend_mtd',        w: 1, h: 1, scope: 'DEPT', missing: true, requiresBackend: 'GET /api/v1/analytics/procurement/spend-mtd?scope=DEPT' },
      { widgetKey: 'procurement.price_change_alerts', w: 1, h: 1, scope: 'DEPT', missing: true, requiresBackend: 'GET /api/v1/analytics/procurement/price-change-alerts?scope=DEPT' },
      { widgetKey: 'inventory.pending_grns',       w: 1, h: 1, scope: 'DEPT', missing: true, requiresBackend: 'GET /api/v1/analytics/inventory/pending-grns?scope=DEPT' },
    ],
  },

  /* ── SERVICE_MANAGER ────────────────────────────────────────────────── */
  SERVICE_MANAGER: {
    role: 'SERVICE_MANAGER',
    title: 'Service Command Centre',
    subtitle: 'Tickets, SLA, technician load, today\u2019s visits.',
    widgets: [
      // Row 1 — urgency
      { widgetKey: 'service.open_tickets',         w: 1, h: 1, scope: 'DEPT' },
      { widgetKey: 'service.sla_breach_count',     w: 1, h: 1, scope: 'DEPT', missing: true, requiresBackend: 'GET /api/v1/analytics/service/sla-breach-count?scope=DEPT' },
      { widgetKey: 'service.visits_today',         w: 1, h: 1, scope: 'DEPT', missing: true, requiresBackend: 'GET /api/v1/analytics/service/visits-today?scope=DEPT' },
      { widgetKey: 'service.warranty_claims',      w: 1, h: 1, scope: 'DEPT' },
      // Row 2 — rolling
      { widgetKey: 'service.mttr',                 w: 1, h: 1, scope: 'DEPT', missing: true, requiresBackend: 'GET /api/v1/analytics/service/mttr?scope=DEPT' },
      { widgetKey: 'service.technician_utilization', w: 1, h: 1, scope: 'DEPT', missing: true, requiresBackend: 'GET /api/v1/analytics/service/technician-utilization?scope=DEPT' },
      { widgetKey: 'service.installation_completion', w: 1, h: 1, scope: 'DEPT', missing: true, requiresBackend: 'GET /api/v1/analytics/service/installation-completion?scope=DEPT' },
      { widgetKey: 'owner.warranty_claim_rate',    w: 1, h: 1, scope: 'DEPT' },
      // Row 3 — leaderboard
      { widgetKey: 'service.tickets_per_technician', w: 4, h: 2, scope: 'DEPT', missing: true, requiresBackend: 'GET /api/v1/analytics/service/tickets-per-technician?scope=DEPT' },
    ],
  },

  /* ── SERVICE_TECHNICIAN ─────────────────────────────────────────────── */
  SERVICE_TECHNICIAN: {
    role: 'SERVICE_TECHNICIAN',
    title: 'My Service Day',
    subtitle: 'Your tickets. Your route. Your parts.',
    widgets: [
      // Row 1 — today
      { widgetKey: 'my.tickets_assigned',          w: 1, h: 1, scope: 'MY' },
      { widgetKey: 'my.tickets_resolved',          w: 1, h: 1, scope: 'MY' },
      { widgetKey: 'my.ticket_resolution',         w: 1, h: 1, scope: 'MY' },
      { widgetKey: 'my.completed_today',           w: 1, h: 1, scope: 'MY', missing: true, requiresBackend: 'GET /api/v1/analytics/my-productivity/completed-today' },
      // Row 2 — route + gear
      { widgetKey: 'my.next_site_visits',          w: 2, h: 2, scope: 'MY', missing: true, requiresBackend: 'GET /api/v1/analytics/my-productivity/next-site-visits?limit=3' },
      { widgetKey: 'my.parts_in_van',              w: 1, h: 1, scope: 'MY', missing: true, requiresBackend: 'GET /api/v1/analytics/my-productivity/parts-in-van' },
      { widgetKey: 'my.mttr',                      w: 1, h: 1, scope: 'MY', missing: true, requiresBackend: 'GET /api/v1/analytics/my-productivity/mttr' },
      // Row 3 — pending verifications
      { widgetKey: 'my.pending_otp_verifications', w: 1, h: 1, scope: 'MY', missing: true, requiresBackend: 'GET /api/v1/analytics/my-productivity/pending-otp-verifications' },
      { widgetKey: 'my.installations_assigned',   w: 1, h: 1, scope: 'MY', missing: true, requiresBackend: 'GET /api/v1/analytics/my-productivity/installations-assigned' },
    ],
  },

  /* ── FINANCE_MANAGER ────────────────────────────────────────────────── */
  FINANCE_MANAGER: {
    role: 'FINANCE_MANAGER',
    title: 'Finance Command Centre',
    subtitle: 'Cash, receivables, compliance, P&L.',
    widgets: [
      // Row 1 — urgency
      { widgetKey: 'finance.invoices_overdue',     w: 1, h: 1, scope: 'DEPT' },
      { widgetKey: 'finance.cash_position',        w: 1, h: 1, scope: 'DEPT', missing: true, requiresBackend: 'GET /api/v1/analytics/finance/cash-position?scope=DEPT' },
      { widgetKey: 'finance.expense_approval_queue', w: 1, h: 1, scope: 'DEPT', missing: true, requiresBackend: 'GET /api/v1/analytics/finance/expense-approval-queue?scope=DEPT' },
      { widgetKey: 'finance.bank_recon_pending',   w: 1, h: 1, scope: 'DEPT', missing: true, requiresBackend: 'GET /api/v1/analytics/finance/bank-recon-pending?scope=DEPT' },
      // Row 2 — rolling
      { widgetKey: 'sales.outstanding',            w: 1, h: 1, scope: 'DEPT' },
      { widgetKey: 'owner.dso',                    w: 1, h: 1, scope: 'DEPT' },
      { widgetKey: 'finance.revenue_invoiced',     w: 1, h: 1, scope: 'DEPT' },
      { widgetKey: 'sales.revenue_achieved',       w: 1, h: 1, scope: 'DEPT' },
      // Row 3 — compliance + deep-dive
      { widgetKey: 'finance.gst_filing_status',    w: 1, h: 1, scope: 'DEPT', missing: true, requiresBackend: 'GET /api/v1/analytics/finance/gst-filing-status?scope=DEPT' },
      { widgetKey: 'finance.pnl_mtd',              w: 1, h: 1, scope: 'DEPT', missing: true, requiresBackend: 'GET /api/v1/analytics/finance/pnl-mtd?scope=DEPT' },
      { widgetKey: 'finance.receivables_aging',    w: 2, h: 2, scope: 'DEPT', missing: true, requiresBackend: 'GET /api/v1/analytics/finance/receivables-aging?scope=DEPT' },
      { widgetKey: 'finance.top_debtors',          w: 2, h: 2, scope: 'DEPT', missing: true, requiresBackend: 'GET /api/v1/analytics/finance/top-debtors?limit=10&scope=DEPT' },
    ],
  },

  /* ── TELECALLER ─────────────────────────────────────────────────────── */
  TELECALLER: {
    role: 'TELECALLER',
    title: 'My Call Sprint',
    subtitle: 'Your queue, your connect rate, your conversions.',
    widgets: [
      // Row 1 — today
      { widgetKey: 'my.calls_today',               w: 1, h: 1, scope: 'MY', missing: true, requiresBackend: 'GET /api/v1/analytics/my-productivity/calls-today' },
      { widgetKey: 'my.calls_target',              w: 1, h: 1, scope: 'MY', missing: true, requiresBackend: 'GET /api/v1/analytics/my-productivity/calls-target' },
      { widgetKey: 'my.connect_rate',              w: 1, h: 1, scope: 'MY', missing: true, requiresBackend: 'GET /api/v1/analytics/my-productivity/connect-rate' },
      { widgetKey: 'my.high_priority_leads_left',  w: 1, h: 1, scope: 'MY', missing: true, requiresBackend: 'GET /api/v1/analytics/my-productivity/high-priority-leads-left' },
      // Row 2 — rolling
      { widgetKey: 'my.meeting_conversion',        w: 1, h: 1, scope: 'MY', missing: true, requiresBackend: 'GET /api/v1/analytics/my-productivity/meeting-conversion' },
      { widgetKey: 'my.call_log_last_hour',        w: 3, h: 2, scope: 'MY', missing: true, requiresBackend: 'GET /api/v1/analytics/my-productivity/call-log?window=1h' },
    ],
  },

  /* ── LEAD_MANAGER ───────────────────────────────────────────────────── */
  /* Apr 28, 2026 — special role: verifies, edits, reassigns leads. Sees ALL
   * leads (verified + unverified) regardless of team scope. Cannot edit
   * deals/invoices. Layout is verification-queue-first. */
  /* ── LEAD_TRIAGE_COORDINATOR ─────────────────────────────────────────
   * May 26, 2026 — Deepika role. Strict view: ONLY /leads/triage + profile.
   * No widgets here — her landing page is /leads/triage, not /dashboard.
   * This entry exists so ROLE_LAYOUTS satisfies `Record<DashboardRoleKey,...>`. */
  LEAD_TRIAGE_COORDINATOR: {
    role: 'LEAD_TRIAGE_COORDINATOR',
    title: 'Lead Triage',
    subtitle: 'Unassigned leads, your queue.',
    widgets: [],
  },

  LEAD_MANAGER: {
    role: 'LEAD_MANAGER',
    title: 'Lead Verification Queue',
    subtitle: 'All leads. Verify, edit, reassign.',
    widgets: [
      // Row 1 — verification urgency (ORG scope — sees everything)
      { widgetKey: 'leads.needs_manual_verify',     w: 1, h: 1, scope: 'ORG', missing: true, requiresBackend: 'GET /api/v1/analytics/leads/needs-manual-verify?scope=ORG' },
      { widgetKey: 'leads.chatkart_stuck',          w: 1, h: 1, scope: 'ORG', missing: true, requiresBackend: 'GET /api/v1/analytics/leads/chatkart-stuck?scope=ORG' },
      { widgetKey: 'leads.pending_reassign_approvals', w: 1, h: 1, scope: 'ORG', missing: true, requiresBackend: 'GET /api/v1/lead-approval-requests?status=pending' },
      { widgetKey: 'leads.unassigned_count',        w: 1, h: 1, scope: 'ORG', missing: true, requiresBackend: 'GET /api/v1/analytics/leads/unassigned?scope=ORG' },
      // Row 2 — rolling
      { widgetKey: 'leads.verified_today',          w: 1, h: 1, scope: 'ORG', missing: true, requiresBackend: 'GET /api/v1/analytics/leads/verified-today?scope=ORG' },
      { widgetKey: 'leads.rejected_today',          w: 1, h: 1, scope: 'ORG', missing: true, requiresBackend: 'GET /api/v1/analytics/leads/rejected-today?scope=ORG' },
      { widgetKey: 'leads.avg_verify_time',         w: 1, h: 1, scope: 'ORG', missing: true, requiresBackend: 'GET /api/v1/analytics/leads/avg-verify-time?scope=ORG' },
      { widgetKey: 'leads.source_mix',              w: 1, h: 1, scope: 'ORG', missing: true, requiresBackend: 'GET /api/v1/analytics/leads/source-mix?scope=ORG' },
      // Row 3 — team workload
      { widgetKey: 'leads.team_load',               w: 4, h: 2, scope: 'ORG', missing: true, requiresBackend: 'GET /api/v1/analytics/leads/team-load?scope=ORG' },
    ],
  },

  /* ── BACKEND_STAFF ──────────────────────────────────────────────────── */
  /* Apr 28, 2026 — formerly TELECALLER. Same UI layout for now (deliberate
   * alias) — call queue, connect rate, conversions. Differentiation lives
   * in role-name + permission middleware (team-scoped lead visibility). */
  BACKEND_STAFF: {
    role: 'BACKEND_STAFF',
    title: 'My Call Sprint',
    subtitle: 'Your queue, your connect rate, your conversions.',
    widgets: [
      // Row 1 — today
      { widgetKey: 'my.calls_today',               w: 1, h: 1, scope: 'MY', missing: true, requiresBackend: 'GET /api/v1/analytics/my-productivity/calls-today' },
      { widgetKey: 'my.calls_target',              w: 1, h: 1, scope: 'MY', missing: true, requiresBackend: 'GET /api/v1/analytics/my-productivity/calls-target' },
      { widgetKey: 'my.connect_rate',              w: 1, h: 1, scope: 'MY', missing: true, requiresBackend: 'GET /api/v1/analytics/my-productivity/connect-rate' },
      { widgetKey: 'my.high_priority_leads_left',  w: 1, h: 1, scope: 'MY', missing: true, requiresBackend: 'GET /api/v1/analytics/my-productivity/high-priority-leads-left' },
      // Row 2 — rolling
      { widgetKey: 'my.meeting_conversion',        w: 1, h: 1, scope: 'MY', missing: true, requiresBackend: 'GET /api/v1/analytics/my-productivity/meeting-conversion' },
      { widgetKey: 'my.call_log_last_hour',        w: 3, h: 2, scope: 'MY', missing: true, requiresBackend: 'GET /api/v1/analytics/my-productivity/call-log?window=1h' },
    ],
  },

  /* ── WAREHOUSE_EXECUTIVE ────────────────────────────────────────────── */
  /* Layout kept minimal — this role has a bespoke dashboard component
   * (WarehouseExecDashboard.tsx) that owns its own layout/data. The entry
   * here exists so ROLE_LAYOUTS satisfies `Record<DashboardRoleKey, ...>`
   * and the legacy widget-grid fallback has something to render if ever hit. */
  WAREHOUSE_EXECUTIVE: {
    role: 'WAREHOUSE_EXECUTIVE',
    title: 'My Floor',
    subtitle: 'Your GRN queue, your dispatch, your cycle counts.',
    widgets: [
      { widgetKey: 'inventory.pending_grns',       w: 1, h: 1, scope: 'MY', missing: true, requiresBackend: 'GET /api/v1/analytics/my-productivity/pending-grns' },
      { widgetKey: 'inventory.movements_today',    w: 1, h: 1, scope: 'MY', missing: true, requiresBackend: 'GET /api/v1/analytics/my-productivity/movements-today' },
      { widgetKey: 'inventory.below_min_stock',    w: 1, h: 1, scope: 'MY' },
      { widgetKey: 'inventory.recent_dispatches',  w: 2, h: 2, scope: 'MY', missing: true, requiresBackend: 'GET /api/v1/analytics/my-productivity/recent-dispatches?limit=5' },
    ],
  },

  /* ── ACCOUNTS_EXECUTIVE ─────────────────────────────────────────────── */
  /* See WAREHOUSE_EXECUTIVE comment — bespoke component owns real layout. */
  ACCOUNTS_EXECUTIVE: {
    role: 'ACCOUNTS_EXECUTIVE',
    title: 'My Desk',
    subtitle: 'Your match queue, your approvals, your GST drafts.',
    widgets: [
      { widgetKey: 'finance.expense_approval_queue', w: 1, h: 1, scope: 'MY', missing: true, requiresBackend: 'GET /api/v1/analytics/my-productivity/expense-approval-queue' },
      { widgetKey: 'finance.bank_recon_pending',   w: 1, h: 1, scope: 'MY', missing: true, requiresBackend: 'GET /api/v1/analytics/my-productivity/bank-recon-pending' },
      { widgetKey: 'finance.invoices_overdue',     w: 1, h: 1, scope: 'MY' },
      { widgetKey: 'sales.outstanding',            w: 1, h: 1, scope: 'MY' },
    ],
  },

  /* ── FIELD_PARTNER ──────────────────────────────────────────────────── */
  /* See WAREHOUSE_EXECUTIVE comment — bespoke component owns real layout. */
  FIELD_PARTNER: {
    role: 'FIELD_PARTNER',
    title: 'My Work',
    subtitle: 'Your installations, your claims, your payouts.',
    widgets: [
      { widgetKey: 'my.installations_assigned',    w: 1, h: 1, scope: 'MY', missing: true, requiresBackend: 'GET /api/v1/analytics/my-productivity/installations-assigned' },
      { widgetKey: 'my.completed_today',           w: 1, h: 1, scope: 'MY', missing: true, requiresBackend: 'GET /api/v1/analytics/my-productivity/completed-today' },
      { widgetKey: 'my.pending_otp_verifications', w: 1, h: 1, scope: 'MY', missing: true, requiresBackend: 'GET /api/v1/analytics/my-productivity/pending-otp-verifications' },
      { widgetKey: 'my.next_site_visits',          w: 2, h: 2, scope: 'MY', missing: true, requiresBackend: 'GET /api/v1/analytics/my-productivity/next-site-visits?limit=3' },
    ],
  },
};

/* ---------------------------------------------------------------------- */
/*  Role resolution                                                        */
/* ---------------------------------------------------------------------- */

/**
 * Map a user's (role.name + department + designation) → DashboardRoleKey.
 *
 * Ordering matters — more-specific checks first, coarsest last.
 * Caller should also check `user.role.name === 'admin' && !user.tenantId` BEFORE
 * calling this (platform super-admin → separate admin page, not a role dashboard).
 *
 * Unrecognized identities fail closed; profile role names precede legacy heuristics.
 */
export const PROFILE_ROLE_KEYS: Record<ProfileId, DashboardRoleKey> = {"P01": "SALES_MANAGER", "P02": "SALES_EXECUTIVE", "P03": "LEAD_TRIAGE_COORDINATOR", "P04": "CRM_OPERATIONS", "P05": "PURCHASE_HEAD", "P06": "FINANCE_MANAGER", "P07": "PRODUCTION_HEAD", "P08": "PUF_OPERATOR", "P09": "PACKING_OPERATOR", "P10": "QA_MANAGER", "P11": "QA_INSPECTOR", "P12": "DISPATCH_MANAGER", "P13": "LOADER", "P14": "INVENTORY_HEAD", "P15": "SECURITY", "P16": "SERVICE_TECHNICIAN", "P17": "HR", "P18": "OWNER"};

export function resolveRoleKey(user: UserForRoleResolution | null | undefined): DashboardRoleKey {
  if (!user) return 'UNKNOWN';
  const profile = resolveProfile(user);
  if (profile) return PROFILE_ROLE_KEYS[profile];
  const roleName = normalizeRoleName(user.role?.name);
  if (!isLegacyIdentity(user)) return 'UNKNOWN';
  const deptName = (user.department?.name ?? '').toLowerCase().trim();
  const deptCode = (user.department?.code ?? '').toUpperCase().trim();
  const title = (user.designation ?? '').toLowerCase().trim();

  // Lead Triage Coordinator — Deepika role. ONLY triage dashboard + profile.
  // Runs BEFORE LEAD_MANAGER so it wins for the more-specific role name.
  if (roleName === 'lead_triage_coordinator' || title.includes('lead triage') || title.includes('triage coordinator')) return 'LEAD_TRIAGE_COORDINATOR';

  // Lead Manager — special role. Sees ALL leads. Runs BEFORE every other
  // sales/department check so a "Lead Manager" inside the Sales department
  // doesn't fall through to SALES_MANAGER.
  if (roleName === 'lead_manager' || title.includes('lead manager') || title.includes('lead verifier')) return 'LEAD_MANAGER';

  // Telecaller — role trumps department
  if (roleName === 'telecaller') return 'TELECALLER';

  // Backend Staff — alias for TELECALLER UI for now, but distinct role-name
  // so backend permission middleware can scope leads to TEAM (not OWN).
  if (roleName === 'backend_staff' || title.includes('backend staff')) return 'BACKEND_STAFF';

  // Field Executive — explicit role-name path. Falls through to existing
  // SALES_EXECUTIVE constant since UI is identical.
  if (roleName === 'field_executive' || title.includes('field executive')) return 'SALES_EXECUTIVE';

  // ── Role-name and designation short-circuits (work even when departmentId is null) ──
  // Sun Stellar seed users have User.departmentId=null, so depend on role+title.
  // Supervisor check runs BEFORE the role-name check so a "PUF Supervisor" whose role=production_manager
  // still resolves to PRODUCTION_SUPERVISOR, not PRODUCTION_HEAD.
  if (title.includes('puf') || title.includes('packaging supervisor') || title.includes('shift supervisor') || title.includes('floor supervisor') || title.includes('packaging sup')) return 'PRODUCTION_SUPERVISOR';
  if (roleName === 'production_manager' || title.includes('production head') || title.includes('plant head')) return 'PRODUCTION_HEAD';
  if (roleName === 'service_tech' && (title.includes('manager') || title.includes('head'))) return 'SERVICE_MANAGER';
  if (roleName === 'service_tech' || title.includes('service agent') || title.includes('field technician') || title.includes('service engineer')) return 'SERVICE_TECHNICIAN';
  // Field Partner (installer / channel partner) — standalone, no collision with other roles
  if (roleName === 'field_partner' || title.includes('field partner') || title.includes('channel partner') || title.includes('installer')) return 'FIELD_PARTNER';
  // Warehouse Executive — must run BEFORE the '*manager* → INVENTORY_HEAD' detectors below
  if (roleName === 'warehouse_executive' || title.includes('warehouse executive') || title.includes('store executive') || title.includes('dispatch executive') || title.includes('warehouse operator')) return 'WAREHOUSE_EXECUTIVE';
  if (title.includes('warehouse manager') || title.includes('inventory manager') || title.includes('store manager')) return 'INVENTORY_HEAD';
  if (title.includes('purchase head') || title.includes('purchase manager') || title.includes('procurement head') || title.includes('purchase executive')) return 'PURCHASE_HEAD';
  if (title.includes('service manager') || title.includes('field service manager')) return 'SERVICE_MANAGER';
  if (title.includes('area manager') || title.includes('field manager') || title.includes('sales manager') || title.includes('regional manager')) return 'SALES_MANAGER';
  // Apr 24, 2026 — FIELD + OFFICE merged → SALES_EXECUTIVE (single role).
  if (
    title.includes('field executive') || title.includes('field rep') ||
    title.includes('backend crm') || title.includes('inside sales') || title.includes('tele sales')
  ) return 'SALES_EXECUTIVE';
  // Accounts Executive — must run BEFORE the 'accountant/finance manager → FINANCE_MANAGER' detector below
  if (roleName === 'accounts_executive' || title.includes('accounts executive') || title.includes('ap clerk') || title.includes('accounts assistant') || title.includes('finance executive')) return 'ACCOUNTS_EXECUTIVE';
  if (roleName === 'accountant' || title.includes('accountant') || title.includes('finance manager') || title.includes('cfo') || title.includes('finance head')) return 'FINANCE_MANAGER';

  // Canonical sales-manager aliases may be seeded without a designation or
  // department. Resolve only those sales-specific literals here. Generic
  // `manager` deliberately continues through the established department and
  // designation rules above/below so Service, Warehouse, and Production
  // managers keep their specialized dashboards.
  if (roleName === 'sales_head') return 'SALES_HEAD';
  if (
    roleName === 'sales_manager' ||
    roleName === 'sr_sales_manager' ||
    roleName === 'manager_sales' ||
    roleName === 'team_head'
  ) return 'SALES_MANAGER';

  // ── SALES department ────────────────────────────────────────────────
  const isSales =
    deptCode === 'SALES' ||
    deptName === 'sales' ||
    deptName === 'sales department' ||
    deptName.includes('sales');
  if (isSales) {
    if (title.includes('head') || title.includes('director') || title.includes('cso')) return 'SALES_HEAD';
    if (roleName === 'manager' || title.includes('manager') || title.includes('lead')) return 'SALES_MANAGER';
    // Apr 24, 2026 — field + office merged into SALES_EXECUTIVE.
    if (
      roleName === 'field_executive' || title.includes('field') ||
      roleName === 'backend_crm' || title.includes('inside') || title.includes('backend') || title.includes('crm') ||
      roleName === 'sales_rep'
    ) return 'SALES_EXECUTIVE';
    return 'SALES_EXECUTIVE';
  }

  // ── MANUFACTURING / PRODUCTION ──────────────────────────────────────
  const isMfg =
    deptCode === 'MFG' ||
    deptName === 'manufacturing' ||
    deptName === 'production' ||
    deptName.includes('production');
  if (isMfg) {
    if (roleName === 'production_manager' || title.includes('production manager') || title.includes('head') || title.includes('plant manager')) return 'PRODUCTION_HEAD';
    if (title.includes('supervisor') || title.includes('operator') || title.includes('foreman') || title.includes('shift')) return 'PRODUCTION_SUPERVISOR';
    return 'PRODUCTION_SUPERVISOR';
  }

  // ── SERVICE ─────────────────────────────────────────────────────────
  const isService =
    deptCode === 'SERVICE' ||
    deptName === 'service' ||
    deptName.includes('service') ||
    deptName.includes('field service');
  if (isService) {
    if (roleName === 'manager' || title.includes('manager') || title.includes('head')) return 'SERVICE_MANAGER';
    if (roleName === 'service_tech' || title.includes('technician') || title.includes('agent') || title.includes('engineer')) return 'SERVICE_TECHNICIAN';
    return 'SERVICE_TECHNICIAN';
  }

  // ── INVENTORY / WAREHOUSE ───────────────────────────────────────────
  const isInv =
    deptCode === 'INV' ||
    deptCode === 'WAREHOUSE' ||
    deptName.includes('inventory') ||
    deptName.includes('warehouse');
  if (isInv) return 'INVENTORY_HEAD';

  // ── PROCUREMENT / PURCHASE ──────────────────────────────────────────
  const isProc =
    deptCode === 'PROC' ||
    deptName.includes('purchase') ||
    deptName.includes('procurement');
  if (isProc) return 'PURCHASE_HEAD';

  // ── FINANCE / ACCOUNTING ────────────────────────────────────────────
  const isFinance =
    deptCode === 'FINANCE' ||
    deptCode === 'ACCT' ||
    deptName.includes('finance') ||
    deptName.includes('account');
  if (isFinance) return 'FINANCE_MANAGER';
  if (roleName === 'accountant') return 'FINANCE_MANAGER';

  return roleName === 'manager' ? 'SALES_MANAGER' : 'UNKNOWN';
}

/** Every widgetKey referenced anywhere in ROLE_LAYOUTS — de-duped. */
export const ALL_REFERENCED_WIDGET_KEYS: string[] = Array.from(
  new Set(
    Object.values(ROLE_LAYOUTS).flatMap((layout) => layout.widgets.map((w) => w.widgetKey)),
  ),
);

/** Widget keys that do NOT exist in widgetRegistry today (Squad Bravo builds). */
export const MISSING_WIDGET_KEYS: string[] = Array.from(
  new Set(
    Object.values(ROLE_LAYOUTS)
      .flatMap((layout) => layout.widgets)
      .filter((w) => w.missing)
      .map((w) => w.widgetKey),
  ),
);

/** Every new backend endpoint Squad Bravo needs to implement. */
export const REQUIRED_NEW_BACKEND_ENDPOINTS: string[] = Array.from(
  new Set(
    Object.values(ROLE_LAYOUTS)
      .flatMap((layout) => layout.widgets)
      .map((w) => w.requiresBackend)
      .filter((x): x is string => typeof x === 'string'),
  ),
);
